import { z } from 'zod';
import { parseWebApiResponse } from './common';
const stringOrDateSchema = z.union([z.string(), z.date()]);
const nullableStringOrDateSchema = stringOrDateSchema.nullable().optional();
const entityIdSchema = z.union([z.coerce.number(), z.string()]);
const threadMemberSchema = z
    .object({
    id: entityIdSchema.optional(),
    contactNumber: z.string().nullable().optional(),
    phoneAnalysis: z
        .object({
        suspicious: z.boolean(),
        reasons: z.array(z.string()),
        summary: z.string().nullable().optional(),
    })
        .strip()
        .optional(),
})
    .passthrough();
const activeRecertificationProcessSchema = z
    .object({
    id: z.coerce.number(),
    leaseId: z.coerce.number(),
    personId: z.coerce.number().nullable(),
    apartmentId: z.coerce.number().nullable(),
    recertificationDueDate: z.string(),
    recertificationProgram: z.string(),
    recertificationType: z.string(),
    resideskRecertificationStatus: z.enum(['pending', 'notified', 'overdue']),
    programCode: z.string().nullable(),
})
    .strip();
const threadBaseSchema = z
    .object({
    id: z.coerce.number(),
    type: z.string().nullable().optional(),
    threadListLabel: z.string().nullable().optional(),
    messageHeaderLabel: z.string().nullable().optional(),
    matchString: z.string().nullable().optional(),
    portfolioId: z.coerce.number().nullable().optional(),
    portfolioName: z.string().nullable().optional(),
    accountName: z.string().nullable().optional(),
    resideskPhoneNumber: z.string().nullable().optional(),
    residentAdvocateFirstName: z.string().nullable().optional(),
    threadMembers: z.array(threadMemberSchema).optional(),
    activeRecertificationProcesses: z.array(activeRecertificationProcessSchema).optional(),
    dataValues: z
        .object({
        unreadCount: z.coerce.number().optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
const threadMessageSchema = z
    .object({
    id: entityIdSchema.optional(),
    threadId: z.coerce.number().optional(),
    outboxId: z.coerce.number().optional(),
    scheduledSendId: z.coerce.number().optional(),
    scheduleGroupId: z.string().nullable().optional(),
    text: z.string().nullable().optional(),
    mediaUrl: z.string().nullable().optional(),
    mediaUrls: z.array(z.string()).nullable().optional(),
    direction: z.string().nullable().optional(),
    fromContactType: z.string().nullable().optional(),
    fromId: entityIdSchema.optional(),
    messageType: z.string().nullable().optional(),
    createdAt: nullableStringOrDateSchema,
    sendAt: nullableStringOrDateSchema,
    sendStatus: z.string().nullable().optional(),
    isPendingOutbox: z.boolean().optional(),
    isScheduledSend: z.boolean().optional(),
    isServiceRequest: z.boolean().optional(),
    isGoogleReview: z.boolean().optional(),
    isLeaseEvent: z.boolean().optional(),
    isActivity: z.boolean().optional(),
    isResidentEvent: z.boolean().optional(),
    residentEventId: entityIdSchema.optional(),
    residentEventType: z.string().optional(),
    readMessagesUsers: z.array(z.unknown()).optional(),
    user: z
        .object({
        id: entityIdSchema.optional(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
    })
        .passthrough()
        .nullable()
        .optional(),
})
    .passthrough();
const topicSuggestionsSchema = z.record(z.string(), z.unknown());
const suggestedResponseSchema = z.union([
    z.string(),
    z
        .object({
        response: z.string().optional(),
        suggestion: z.string().optional(),
        score: z.coerce.number().optional(),
    })
        .passthrough(),
]);
export const threadListResponseSchema = z.array(threadBaseSchema);
export const threadDetailResponseSchema = z.tuple([
    threadBaseSchema,
    z.array(threadMessageSchema),
    topicSuggestionsSchema.optional(),
    z.array(suggestedResponseSchema).nullable().optional(),
]);
export const textableEntitiesResponseSchema = z
    .object({
    textablePeople: z.array(z.unknown()).default([]),
    textableUnits: z.array(z.unknown()).default([]),
    textablePastTenants: z.array(z.unknown()).default([]),
    textableFutureTenants: z.array(z.unknown()).default([]),
})
    .passthrough();
export const threadCreateResponseSchema = z.coerce.number();
export const threadPortfolioResponseSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string().nullable().optional(),
})
    .passthrough();
export const threadOptOutResponseSchema = z
    .object({
    optOut: z.boolean().optional(),
    isDelinquencyOptedOut: z.boolean().optional(),
})
    .passthrough();
export function parseThreadListResponse(data, endpoint = 'GET /api/threads') {
    return parseWebApiResponse(threadListResponseSchema, data, endpoint);
}
export function parseThreadDetailResponse(data) {
    return parseWebApiResponse(threadDetailResponseSchema, data, 'GET /api/threads/:id');
}
export function parseTextableEntitiesResponse(data) {
    return parseWebApiResponse(textableEntitiesResponseSchema, data, 'GET /api/threads/textable/all');
}
export function parseThreadCreateResponse(data) {
    return parseWebApiResponse(threadCreateResponseSchema, data, 'POST /api/threads');
}
export function parseThreadPortfolioResponse(data) {
    return parseWebApiResponse(threadPortfolioResponseSchema, data, 'GET /api/threads/:id/portfolio');
}
export function parseThreadOptOutResponse(data) {
    return parseWebApiResponse(threadOptOutResponseSchema, data, 'POST /api/threads/:id/opt-out');
}
